'use client';

import React from 'react';
import {
  InfoCircledIcon,
  PlusIcon,
  MinusIcon,
  ArrowRightIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';

import { Box, BoxProps, Text, Heading, Badge, Button, Spinner, EmptyState } from '../../index';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table';
import { Code } from '../code';
import { formatDate } from '../../utils/date';

export interface AuditEntry {
  id: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  action: 'create' | 'update' | 'delete' | 'restore' | 'archive';
  changes?: Array<{
    field: string;
    fieldLabel?: string;
    oldValue: unknown;
    newValue: unknown;
    type?: 'string' | 'number' | 'boolean' | 'date' | 'json' | 'array';
  }>;
  metadata?: {
    ip?: string;
    userAgent?: string;
    location?: string;
    source?: string;
    reason?: string;
    [key: string]: unknown;
  };
  actor: {
    id: string;
    name: string;
    email?: string;
    role?: string;
  };
  timestamp: Date | string;
  version?: number;
}

export interface AuditTrailProps extends BoxProps {
  /** Audit entries to display */
  entries: AuditEntry[];
  /** Whether to show loading state */
  loading?: boolean;
  /** Loading more entries */
  loadingMore?: boolean;
  /** Whether there are more entries to load */
  hasMore?: boolean;
  /** Callback to load more entries */
  onLoadMore?: () => void;
  /** Whether to show change details */
  showChanges?: boolean;
  /** Whether to show metadata */
  showMetadata?: boolean;
  /** Whether to group by entity */
  groupByEntity?: boolean;
  /** Custom empty state */
  emptyState?: React.ReactNode;
  /** Entry click handler */
  onEntryClick?: (entry: AuditEntry) => void;
  /** Export handler */
  onExport?: () => void;
  /** Locale for date formatting */
  locale?: string;
  /** Compact mode */
  compact?: boolean;
}

/**
 * AuditTrail component - displays audit log entries with change tracking
 * Supports detailed change history, metadata, and export functionality
 */
export const AuditTrail: React.FC<AuditTrailProps> = ({
  entries,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  showChanges = true,
  showMetadata = false,
  groupByEntity = false,
  emptyState,
  onEntryClick,
  onExport,
  locale = 'en-US',
  compact = false,
  ...boxProps
}) => {
  const [expandedEntries, setExpandedEntries] = React.useState<Set<string>>(new Set());

  // Group entries by entity if requested - moved to top to comply with Rules of Hooks
  const groupedEntries = React.useMemo(() => {
    if (!groupByEntity) return { ungrouped: entries };

    const groups: Record<string, AuditEntry[]> = {};
    entries.forEach((entry) => {
      const key = `${entry.entityType}:${entry.entityId}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(entry);
    });
    return groups;
  }, [entries, groupByEntity]);

  // Action labels in Turkish
  const actionLabels: Record<string, string> = {
    create: 'Oluşturuldu',
    update: 'Güncellendi',
    delete: 'Silindi',
    restore: 'Geri Yüklendi',
    archive: 'Arşivlendi',
  };

  // Action colors (unused but kept for potential future use)
  // const actionColors: Record<string, string> = {
  //   create: 'green',
  //   update: 'blue',
  //   delete: 'red',
  //   restore: 'purple',
  //   archive: 'orange',
  // };

  // Toggle entry expansion
  const toggleExpanded = (entryId: string) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  // Format value based on type
  const formatValue = (value: unknown, type?: string): React.ReactNode => {
    if (value === null || value === undefined) {
      return (
        <Text as="span" color="fg.muted" fontSize="sm">
          boş
        </Text>
      );
    }

    switch (type) {
      case 'date': {
        const dateOptions: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        };
        return formatDate(new Date(value as string), locale, dateOptions);
      }
      case 'boolean':
        return value ? 'Evet' : 'Hayır';
      case 'json':
      case 'array':
        return <Code size="sm">{JSON.stringify(value, null, 2)}</Code>;
      default:
        return String(value);
    }
  };

  // Render change item
  const renderChange = (change: NonNullable<AuditEntry['changes']>[0]) => {
    const isCreation = change.oldValue === null || change.oldValue === undefined;
    const isDeletion = change.newValue === null || change.newValue === undefined;

    return (
      <Box
        key={change.field}
        padding={2}
        backgroundColor="bg.muted"
        borderRadius="md"
        fontSize="sm"
      >
        <Text fontWeight="medium" marginBottom={1}>
          {change.fieldLabel || change.field}
        </Text>

        {isCreation ? (
          <Box display="flex" alignItems="center" gap="2">
            <PlusIcon color="green" />
            <Box>{formatValue(change.newValue, change.type)}</Box>
          </Box>
        ) : isDeletion ? (
          <Box display="flex" alignItems="center" gap="2">
            <MinusIcon color="red" />
            <Box textDecoration="line-through" opacity={0.7}>
              {formatValue(change.oldValue, change.type)}
            </Box>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap="2" flexWrap="wrap">
            <Box textDecoration="line-through" opacity={0.7}>
              {formatValue(change.oldValue, change.type)}
            </Box>
            <ArrowRightIcon />
            <Box fontWeight="medium">{formatValue(change.newValue, change.type)}</Box>
          </Box>
        )}
      </Box>
    );
  };

  // Render metadata
  const renderMetadata = (metadata: AuditEntry['metadata']) => {
    if (!metadata) return null;

    return (
      <Box padding={3} backgroundColor="bg.muted" borderRadius="md" fontSize="sm">
        <Box display="flex" flexDirection="column" gap="1">
          {metadata.ip && (
            <Text>
              <Text as="span" fontWeight="medium">
                IP Adresi:
              </Text>{' '}
              {metadata.ip}
            </Text>
          )}
          {metadata.location && (
            <Text>
              <Text as="span" fontWeight="medium">
                Konum:
              </Text>{' '}
              {metadata.location}
            </Text>
          )}
          {metadata.source && (
            <Text>
              <Text as="span" fontWeight="medium">
                Kaynak:
              </Text>{' '}
              {metadata.source}
            </Text>
          )}
          {metadata.reason && (
            <Text>
              <Text as="span" fontWeight="medium">
                Sebep:
              </Text>{' '}
              {metadata.reason}
            </Text>
          )}
          {metadata.userAgent && (
            <Text fontSize="xs" color="fg.muted" marginTop={2}>
              {metadata.userAgent}
            </Text>
          )}
        </Box>
      </Box>
    );
  };

  // Render single entry
  const renderEntry = (entry: AuditEntry) => {
    const isExpanded = expandedEntries.has(entry.id);
    const hasChanges = entry.changes && entry.changes.length > 0;
    const canExpand = (hasChanges && showChanges) || (entry.metadata && showMetadata);

    if (compact) {
      return (
        <TableRow
          key={entry.id}
          cursor={onEntryClick ? 'pointer' : 'default'}
          _hover={onEntryClick ? { backgroundColor: 'bg.muted' } : undefined}
          onClick={() => onEntryClick?.(entry)}
        >
          <TableCell>
            {formatDate(new Date(entry.timestamp), locale, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </TableCell>
          <TableCell>
            <Badge size="sm">{actionLabels[entry.action]}</Badge>
          </TableCell>
          <TableCell>
            <Text fontWeight="medium">{entry.entityName || entry.entityId}</Text>
            <Text fontSize="xs" color="fg.muted">
              {entry.entityType}
            </Text>
          </TableCell>
          <TableCell>{entry.actor.name}</TableCell>
          {hasChanges && showChanges && (
            <TableCell>
              <Text fontSize="sm" color="fg.muted">
                {entry.changes?.length || 0} değişiklik
              </Text>
            </TableCell>
          )}
        </TableRow>
      );
    }

    return (
      <Box
        key={entry.id}
        backgroundColor="bg.surface"
        borderRadius="lg"
        border="1px solid"
        borderColor="border.muted"
        overflow="hidden"
      >
        {/* Entry header */}
        <Box
          padding={4}
          cursor={canExpand ? 'pointer' : 'default'}
          onClick={() => canExpand && toggleExpanded(entry.id)}
          _hover={canExpand ? { backgroundColor: 'bg.muted' } : undefined}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap="2" marginBottom={2}>
                <Badge>{actionLabels[entry.action]}</Badge>
                <Text fontSize="sm" color="fg.muted">
                  {formatDate(new Date(entry.timestamp), locale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
                {entry.version && <Badge size="sm">v{entry.version}</Badge>}
              </Box>

              <Text marginBottom={1}>
                <Text as="span" fontWeight="semibold">
                  {entry.actor.name}
                </Text>
                {entry.actor.role && (
                  <Text as="span" color="fg.muted">
                    {' '}
                    ({entry.actor.role})
                  </Text>
                )}{' '}
                <Text as="span" color="fg.muted">
                  {entry.entityType} kayıtında değişiklik yaptı:
                </Text>{' '}
                <Text as="span" fontWeight="medium">
                  {entry.entityName || entry.entityId}
                </Text>
              </Text>

              {hasChanges && showChanges && !isExpanded && (
                <Text fontSize="sm" color="fg.muted">
                  {entry.changes?.length || 0} alan değiştirildi
                </Text>
              )}
            </Box>

            {canExpand && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(entry.id);
                }}
              >
                {isExpanded ? 'Gizle' : 'Detayları Göster'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Expanded content */}
        {isExpanded && (
          <Box borderTop="1px solid" borderColor="border.muted" padding={4}>
            <Box display="flex" flexDirection="column" gap="4">
              {/* Changes */}
              {hasChanges && showChanges && (
                <Box>
                  <Heading size="sm" marginBottom={3}>
                    Değişiklikler ({entry.changes?.length || 0})
                  </Heading>
                  <Box display="flex" flexDirection="column" gap="2">
                    {entry.changes?.map(renderChange)}
                  </Box>
                </Box>
              )}

              {/* Metadata */}
              {entry.metadata && showMetadata && (
                <Box>
                  <Heading size="sm" marginBottom={3}>
                    Ek Bilgiler
                  </Heading>
                  {renderMetadata(entry.metadata)}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  if (loading && entries.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" padding={8} {...boxProps}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (!loading && entries.length === 0) {
    return (
      <Box {...boxProps}>
        {emptyState || (
          <EmptyState
            title="Denetim kaydı bulunamadı"
            description="Sistem üzerinde gerçekleşen değişiklikler burada görüntülenecek."
            icon={<InfoCircledIcon />}
          />
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="1rem" {...boxProps}>
      {/* Header with export */}
      {onExport && (
        <Box display="flex" justifyContent="flex-end">
          <Button size="sm" variant="outline" leftIcon={<DownloadIcon />} onClick={onExport}>
            Dışa Aktar
          </Button>
        </Box>
      )}

      {/* Compact table view */}
      {compact ? (
        <Box overflowX="auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>İşlem</TableHead>
                <TableHead>Kayıt</TableHead>
                <TableHead>Kullanıcı</TableHead>
                {showChanges && <TableHead>Değişiklikler</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>{entries.map(renderEntry)}</TableBody>
          </Table>
        </Box>
      ) : (
        /* Standard view */
        <Box display="flex" flexDirection="column" gap="3">
          {Object.entries(groupedEntries).map(([key, groupEntries]) => (
            <Box key={key}>
              {groupByEntity && key !== 'ungrouped' && (
                <Heading size="sm" marginBottom={3} color="fg.muted">
                  {groupEntries[0].entityType}:{' '}
                  {groupEntries[0].entityName || groupEntries[0].entityId}
                </Heading>
              )}
              <Box display="flex" flexDirection="column" gap="3">
                {groupEntries.map(renderEntry)}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Load more button */}
      {hasMore && onLoadMore && (
        <Box display="flex" justifyContent="center" marginTop={4}>
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={loadingMore}
            leftIcon={loadingMore ? <Spinner size="sm" /> : undefined}
          >
            {loadingMore ? 'Yükleniyor...' : 'Daha Fazla Yükle'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AuditTrail;
